import type {
  AriaAttributes,
  ChangeEvent,
  HTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { InlineError } from "./Feedback";

type FieldControlProps = AriaAttributes & {
  id?: string;
};

function describedBy(...ids: Array<string | undefined>): string | undefined {
  const value = ids.filter(Boolean).join(" ");
  return value.length > 0 ? value : undefined;
}

function isFieldControl(
  child: ReactNode,
): child is ReactElement<FieldControlProps> {
  return (
    isValidElement<FieldControlProps>(child) &&
    (child.type === "input" ||
      child.type === "select" ||
      child.type === "textarea")
  );
}

function findControlId(children: ReactNode): string | undefined {
  let controlId: string | undefined;

  Children.forEach(children, (child) => {
    if (
      !controlId &&
      isFieldControl(child) &&
      typeof child.props.id === "string"
    ) {
      controlId = child.props.id;
    }
  });

  return controlId;
}

function useResolvedInputId(
  id: string | undefined,
  name: string | undefined,
): string {
  const generatedId = useId();
  return id ?? name ?? generatedId;
}

export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  // A `HelpTip` rendered beside the label (UI-LAW §4d). Kept out of the
  // `<label>` element so the control's accessible name stays the label text.
  help?: ReactNode;
  htmlFor?: string;
  label: string;
  // Renders the design-system required mark beside the label (#1441); the
  // wrapped control still carries its own native/aria required wiring.
  required?: boolean;
}

// The one required-field mark (never re-implemented per screen). Rendered
// beside — never inside — the label element, so accessible names stay clean;
// the control's `required`/`aria-required` already announces the state.
function RequiredMark({ required }: { required?: boolean }) {
  if (!required) {
    return null;
  }
  return (
    <span aria-hidden="true" className="marker-field__required">
      *
    </span>
  );
}

export function Field({
  children,
  className,
  description,
  error,
  help,
  htmlFor,
  label,
  required,
  ...props
}: FieldProps) {
  const generatedId = useId();
  const inputId = htmlFor ?? findControlId(children) ?? generatedId;
  const descriptionId =
    inputId && description ? `${inputId}-description` : undefined;
  const errorId = inputId && error ? `${inputId}-error` : undefined;
  const classes = ["marker-field"];
  let controlIsWired = false;
  if (className) {
    classes.push(className);
  }

  return (
    <div className={classes.join(" ")} {...props}>
      <span className="marker-field__label-row">
        <label className="marker-field__label" htmlFor={inputId}>
          {label}
        </label>
        <RequiredMark required={required} />
        {help}
      </span>
      {Children.map(children, (child) => {
        if (controlIsWired || !isFieldControl(child)) {
          return child;
        }

        controlIsWired = true;
        return cloneElement(child, {
          "aria-describedby": describedBy(
            child.props["aria-describedby"],
            descriptionId,
            errorId,
          ),
          "aria-invalid": error ? true : child.props["aria-invalid"],
          id: child.props.id ?? inputId,
        });
      })}
      {description ? (
        <span className="marker-field__description" id={descriptionId}>
          {description}
        </span>
      ) : null}
      {error ? <InlineError id={errorId}>{error}</InlineError> : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  description?: ReactNode;
  error?: ReactNode;
  help?: ReactNode;
  label: string;
}

export function TextInput({
  className,
  description,
  error,
  help,
  id,
  label,
  name,
  required,
  "aria-describedby": ariaDescribedBy,
  ...props
}: TextInputProps) {
  const inputId = useResolvedInputId(id, name);

  return (
    <Field
      className={className}
      description={description}
      error={error}
      help={help}
      htmlFor={inputId}
      label={label}
      required={required}
    >
      <input
        {...props}
        aria-describedby={ariaDescribedBy}
        className="marker-field__input"
        id={inputId}
        name={name}
        required={required}
      />
    </Field>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>;
  description?: ReactNode;
  error?: ReactNode;
  help?: ReactNode;
  label: string;
}

export function Textarea({
  className,
  description,
  error,
  help,
  id,
  label,
  name,
  required,
  "aria-describedby": ariaDescribedBy,
  ...props
}: TextareaProps) {
  const inputId = useResolvedInputId(id, name);

  return (
    <Field
      className={className}
      description={description}
      error={error}
      help={help}
      htmlFor={inputId}
      label={label}
      required={required}
    >
      <textarea
        {...props}
        aria-describedby={ariaDescribedBy}
        className="marker-field__input marker-field__textarea"
        id={inputId}
        name={name}
        required={required}
      />
    </Field>
  );
}

type SelectValue = string | number | readonly string[];

type SelectOptionElementProps = {
  children?: ReactNode;
  disabled?: boolean;
  label?: string;
  value?: SelectValue;
};

type SelectOption = {
  disabled: boolean;
  label: ReactNode;
  // Plain-text form of `label`, used as the trigger's native `title` fallback
  // when a long value ellipsizes (UI-LAW §4a: Tooltip-grade truncation label).
  text: string;
  value: string;
  /** `<optgroup label>` this option belongs to; renders as a non-interactive
   * group header row in the listbox when it changes. */
  group?: string;
};

function nodeText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return Children.toArray(node)
      .map((entry) => nodeText(entry))
      .join("");
  }
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return nodeText(node.props.children);
  }
  return "";
}

function selectValue(value: SelectValue | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value[0] === undefined ? undefined : String(value[0]);
  }
  return String(value);
}

function parseSelectOptions(
  children: ReactNode,
  group?: string,
): SelectOption[] {
  const options: SelectOption[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement<SelectOptionElementProps>(child)) {
      return;
    }

    if (child.type === Fragment) {
      options.push(...parseSelectOptions(child.props.children, group));
      return;
    }

    if (child.type === "optgroup") {
      options.push(
        ...parseSelectOptions(child.props.children, child.props.label),
      );
      return;
    }

    if (child.type !== "option") {
      return;
    }

    const textLabel =
      child.props.label ?? nodeText(child.props.children).trim();
    const value = selectValue(child.props.value) ?? textLabel;
    options.push({
      disabled: child.props.disabled === true,
      label: child.props.children ?? child.props.label ?? value,
      text: textLabel === "" ? value : textLabel,
      value,
      group,
    });
  });

  return options;
}

function firstEnabledOption(
  options: readonly SelectOption[],
): SelectOption | null {
  return options.find((option) => !option.disabled) ?? null;
}

function nextEnabledIndex(
  options: readonly SelectOption[],
  from: number,
  step: 1 | -1,
): number {
  const count = options.length;
  if (count === 0) {
    return -1;
  }
  for (let offset = 1; offset <= count; offset += 1) {
    const index = (from + step * offset + count * offset) % count;
    if (options[index]?.disabled !== true) {
      return index;
    }
  }
  return -1;
}

function createSelectChangeEvent(
  value: string,
): ChangeEvent<HTMLSelectElement> {
  const target = { value } as HTMLSelectElement;
  return { currentTarget: target, target } as ChangeEvent<HTMLSelectElement>;
}

function ChevronIcon() {
  return (
    <svg
      aria-hidden="true"
      className="marker-select__icon"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="marker-select__check"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="m5 12 5 5 9-11" />
    </svg>
  );
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "children" | "defaultValue" | "onChange" | "size" | "value"
> {
  children: ReactNode;
  defaultValue?: SelectValue;
  description?: ReactNode;
  emptyLabel?: string;
  error?: ReactNode;
  help?: ReactNode;
  label: string;
  /** Keeps the label for assistive tech but off the visual row — toolbar
   * selects sit flush with sibling sm Buttons instead of stacking a caps
   * label above (the `.marker-data-table` filter-select precedent). */
  labelHidden?: boolean;
  onChange?: SelectHTMLAttributes<HTMLSelectElement>["onChange"];
  size?: "md" | "sm";
  value?: SelectValue;
}

export function Select({
  children,
  className,
  defaultValue,
  description,
  disabled = false,
  emptyLabel = "No options.",
  error,
  help,
  id,
  label,
  labelHidden = false,
  name,
  onChange,
  required,
  size = "md",
  value,
  "aria-describedby": ariaDescribedBy,
}: SelectProps) {
  const generatedId = useId();
  const baseId = id ?? name ?? generatedId;
  const labelId = `${baseId}-label`;
  const listboxId = `${baseId}-listbox`;
  const descriptionId = description ? `${baseId}-description` : undefined;
  const errorId = error ? `${baseId}-error` : undefined;
  const parsedOptions = useMemo(() => parseSelectOptions(children), [children]);
  // An empty-value child supplies the trigger's unselected label, but is not
  // itself an option. The listbox contains only values the user can select;
  // choosing the current value again returns to this unselected state.
  const unselectedOption =
    parsedOptions.find((option) => option.value === "") ?? null;
  const options = useMemo(
    () => parsedOptions.filter((option) => option.value !== ""),
    [parsedOptions],
  );
  const firstOption = firstEnabledOption(options);
  const controlledValue = selectValue(value);
  const [internalValue, setInternalValue] = useState(
    () =>
      selectValue(defaultValue) ??
      (unselectedOption === null ? (firstOption?.value ?? "") : ""),
  );
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledValue !== undefined;
  const resolvedInternalValue =
    internalValue === "" && unselectedOption !== null
      ? ""
      : options.length > 0 &&
          !options.some((option) => option.value === internalValue)
        ? unselectedOption === null
          ? (firstOption?.value ?? "")
          : ""
        : internalValue;
  const selectedValue = controlledValue ?? resolvedInternalValue;
  const selectedOption =
    selectedValue === ""
      ? unselectedOption
      : (options.find((option) => option.value === selectedValue) ?? null);
  const selectedIndex = selectedOption
    ? options.findIndex((option) => option.value === selectedOption.value)
    : -1;

  const closePopover = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const openPopover = useCallback((index: number) => {
    setOpen(true);
    setActiveIndex(index);
  }, []);

  const commit = useCallback(
    (nextValue: string) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      onChange?.(createSelectChangeEvent(nextValue));
    },
    [isControlled, onChange],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const node = containerRef.current;
      if (
        node &&
        event.target instanceof Node &&
        !node.contains(event.target)
      ) {
        closePopover();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [closePopover, open]);

  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }
    const listbox = listboxRef.current;
    const activeEl = listbox?.querySelector<HTMLElement>(
      '[data-active="true"]',
    );
    if (activeEl && typeof activeEl.scrollIntoView === "function") {
      activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, open]);

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (!open) {
          openPopover(
            selectedIndex >= 0
              ? selectedIndex
              : nextEnabledIndex(options, -1, 1),
          );
          return;
        }
        setActiveIndex((current) => nextEnabledIndex(options, current, 1));
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (!open) {
          openPopover(
            selectedIndex >= 0
              ? selectedIndex
              : nextEnabledIndex(options, 0, -1),
          );
          return;
        }
        setActiveIndex((current) => nextEnabledIndex(options, current, -1));
        return;
      }
      case "Home": {
        if (open) {
          event.preventDefault();
          setActiveIndex(nextEnabledIndex(options, -1, 1));
        }
        return;
      }
      case "End": {
        if (open) {
          event.preventDefault();
          setActiveIndex(nextEnabledIndex(options, 0, -1));
        }
        return;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        if (!open) {
          openPopover(
            selectedIndex >= 0
              ? selectedIndex
              : nextEnabledIndex(options, -1, 1),
          );
          return;
        }
        const activeOption = options[activeIndex];
        if (activeOption && !activeOption.disabled) {
          commit(
            activeOption.value === selectedValue && unselectedOption !== null
              ? ""
              : activeOption.value,
          );
          closePopover();
        }
        return;
      }
      case "Escape": {
        if (open) {
          event.preventDefault();
          closePopover();
        }
        return;
      }
      default:
    }
  };

  const rootClasses = ["marker-field", "marker-select"];
  if (className) {
    rootClasses.push(className);
  }

  const triggerClasses = [
    "marker-field__input",
    "marker-field__select",
    `marker-field__input--${size}`,
    "marker-select__trigger",
  ];
  if (open) {
    triggerClasses.push("marker-select__trigger--open");
  }

  const activeDescendant =
    open && activeIndex >= 0 && options[activeIndex]
      ? `${listboxId}-option-${String(activeIndex)}`
      : undefined;

  return (
    <div className={rootClasses.join(" ")}>
      <span
        className={
          labelHidden
            ? "marker-field__label-row marker-field__label--hidden"
            : "marker-field__label-row"
        }
      >
        <span className="marker-field__label" id={labelId}>
          {label}
        </span>
        <RequiredMark required={required} />
        {help}
      </span>
      <div className="marker-select__anchor" ref={containerRef}>
        <div
          aria-activedescendant={activeDescendant}
          aria-controls={listboxId}
          aria-describedby={describedBy(
            ariaDescribedBy,
            descriptionId,
            errorId,
          )}
          aria-disabled={disabled || undefined}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={error ? true : undefined}
          aria-labelledby={labelId}
          aria-required={required || undefined}
          className={triggerClasses.join(" ")}
          id={baseId}
          onClick={() => {
            if (disabled) {
              return;
            }
            if (open) {
              closePopover();
            } else {
              openPopover(
                selectedIndex >= 0
                  ? selectedIndex
                  : nextEnabledIndex(options, -1, 1),
              );
            }
          }}
          onKeyDown={onTriggerKeyDown}
          ref={triggerRef}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
        >
          <span
            className="marker-select__value"
            title={selectedOption?.text ?? emptyLabel}
          >
            {selectedOption?.label ?? emptyLabel}
          </span>
          <ChevronIcon />
        </div>
        {name || required ? (
          <input
            aria-hidden="true"
            className="marker-select__native-value"
            disabled={disabled}
            name={name}
            onInvalid={(event) => {
              event.preventDefault();
              triggerRef.current?.focus();
            }}
            readOnly
            required={required}
            tabIndex={-1}
            type="text"
            value={selectedValue}
          />
        ) : null}
        {open ? (
          <div className="marker-scope-popover marker-select__popover">
            <div
              aria-labelledby={labelId}
              className="marker-select__listbox"
              id={listboxId}
              ref={listboxRef}
              role="listbox"
            >
              {options.length === 0 ? (
                <div className="marker-select__empty">{emptyLabel}</div>
              ) : (
                options.map((option, index) => {
                  const isSelected = option.value === selectedValue;
                  const isActive = index === activeIndex;
                  const optionId = `${listboxId}-option-${String(index)}`;
                  const optionClasses = ["marker-select__option"];
                  if (isActive) {
                    optionClasses.push("marker-select__option--active");
                  }
                  const groupHeader =
                    option.group !== undefined &&
                    option.group !== options[index - 1]?.group ? (
                      <div
                        aria-hidden="true"
                        className="marker-select__group-label"
                      >
                        {option.group}
                      </div>
                    ) : null;
                  return (
                    <Fragment key={`${option.value}-${String(index)}`}>
                      {groupHeader}
                      <button
                        aria-selected={isSelected}
                        className={optionClasses.join(" ")}
                        data-active={isActive || undefined}
                        disabled={option.disabled}
                        id={optionId}
                        onClick={() => {
                          commit(
                            isSelected && unselectedOption !== null
                              ? ""
                              : option.value,
                          );
                          closePopover();
                          triggerRef.current?.focus();
                        }}
                        onMouseEnter={() => {
                          if (!option.disabled) {
                            setActiveIndex(index);
                          }
                        }}
                        role="option"
                        tabIndex={-1}
                        type="button"
                      >
                        <span className="marker-select__option-label">
                          {option.label}
                        </span>
                        <span className="marker-select__option-check">
                          {isSelected ? <CheckIcon /> : null}
                        </span>
                      </button>
                    </Fragment>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
      {description ? (
        <span className="marker-field__description" id={descriptionId}>
          {description}
        </span>
      ) : null}
      {error ? <InlineError id={errorId}>{error}</InlineError> : null}
    </div>
  );
}
